import type { DetectionRequest, DetectionResult } from './detectionCore.ts'
import { runDetection } from './detectionCore.ts'
import type { EstimateRequest, EstimateResult } from './estimationCore.ts'
import { runEstimation } from './estimationCore.ts'
import type { DiagnoseRequest, DiagnoseResult } from './diagnosisCore.ts'
import { runDiagnosis } from './diagnosisCore.ts'
import type { CensusRequest, CensusResult } from './archiveCensus.ts'
import { runCensus } from './archiveCensus.ts'
import type { RunsRequest, RunsResult } from './plateRuns.ts'
import { runRuns } from './plateRuns.ts'

type Outgoing = { ready: true } | { id: number; result?: unknown; error?: string }
type Kind = 'detect' | 'estimate' | 'diagnose' | 'census' | 'runs'
type Request = DetectionRequest | EstimateRequest | DiagnoseRequest | CensusRequest | RunsRequest

export type WorkerLike = Pick<Worker, 'postMessage' | 'terminate'> & {
  onmessage: ((event: MessageEvent<Outgoing>) => void) | null
  onerror: ((event: unknown) => void) | null
  onmessageerror: ((event: unknown) => void) | null
}

export type DetectionClientOptions = {
  /** `null` where no worker can be made. */
  createWorker: () => WorkerLike | null
  /** How long one message may go unanswered before the worker counts as hung. */
  timeoutMs: number
  local: {
    detect: (request: DetectionRequest) => Promise<DetectionResult>
    estimate: (request: EstimateRequest) => Promise<EstimateResult>
    diagnose: (request: DiagnoseRequest) => Promise<DiagnoseResult>
    census: (request: CensusRequest) => Promise<CensusResult>
    runs: (request: RunsRequest) => Promise<RunsResult>
  }
}

const NO_WORKER = Symbol('no worker')

/**
 * Read scans in a Web Worker. Only where no worker can be had (none here, or one that fails to load) does
 * the work run on the main thread: a loaded worker that fails or hangs passes the error on and is
 * terminated, since the main thread would only freeze on the same input.
 */
export const createDetectionClient = ({ createWorker, timeoutMs, local }: DetectionClientOptions) => {
  let worker: WorkerLike | null = null
  let ready = false
  let unavailable = false
  let nextId = 0
  const pending = new Map<
    number,
    { resolve: (result: unknown) => void; reject: (error: unknown) => void; timer: ReturnType<typeof setTimeout> }
  >()

  const dropWorker = (error: unknown) => {
    pending.forEach((waiting) => {
      clearTimeout(waiting.timer)
      waiting.reject(error)
    })
    pending.clear()
    worker?.terminate()
    worker = null
    ready = false
  }

  /* before the worker said it loaded, a failure is the worker's loading, not the work */
  const failed = (event: unknown) => {
    if (ready) {
      console.warn('Order wizard detection worker failed', event)
      dropWorker(new Error('the detection worker stopped'))
    } else {
      console.warn('Order wizard detection worker could not load; running on the main thread', event)
      unavailable = true
      dropWorker(NO_WORKER)
    }
  }

  const start = (): WorkerLike | null => {
    try {
      const created = createWorker()
      if (!created) {
        unavailable = true
        return null
      }
      created.onmessage = (event) => {
        const data = event.data
        if ('ready' in data) {
          ready = true
          return
        }
        const waiting = pending.get(data.id)
        pending.delete(data.id)
        if (!waiting) {
          return
        }
        clearTimeout(waiting.timer)
        if (data.error === undefined) {
          waiting.resolve(data.result)
        } else {
          waiting.reject(new Error(data.error))
        }
      }
      created.onerror = failed
      created.onmessageerror = failed
      return created
    } catch (error) {
      console.warn('Order wizard detection worker could not start; running on the main thread', error)
      unavailable = true
      return null
    }
  }

  const inWorker = async <R>(kind: Kind, request: Request): Promise<R | typeof NO_WORKER> => {
    if (unavailable) {
      return NO_WORKER
    }
    worker ??= start()
    const current = worker
    if (!current) {
      return NO_WORKER
    }
    try {
      return (await new Promise<unknown>((resolve, reject) => {
        const id = nextId++
        const timer = setTimeout(() => {
          console.warn(`Order wizard detection worker did not answer a ${kind} request in time`)
          dropWorker(new Error('the detection worker did not answer in time'))
        }, timeoutMs)
        pending.set(id, { resolve, reject, timer })
        current.postMessage({ id, kind, request })
      })) as R
    } catch (error) {
      if (error === NO_WORKER) {
        return NO_WORKER
      }
      throw error
    }
  }

  const either = async <R>(kind: Kind, request: Request, onMainThread: () => Promise<R>): Promise<R> => {
    const result = await inWorker<R>(kind, request)
    return result === NO_WORKER ? onMainThread() : result
  }

  return {
    detect: (request: DetectionRequest) => either<DetectionResult>('detect', request, () => local.detect(request)),
    estimate: (request: EstimateRequest) => either('estimate', request, () => local.estimate(request)),
    diagnose: (request: DiagnoseRequest) => either('diagnose', request, () => local.diagnose(request)),
    census: (request: CensusRequest) => either('census', request, () => local.census(request)),
    readRuns: (request: RunsRequest) => either('runs', request, () => local.runs(request))
  }
}

const WORKER_TIMEOUT_MS = 120_000

const client = createDetectionClient({
  createWorker: () =>
    typeof Worker === 'undefined'
      ? null
      : (new Worker(new URL('./detection.worker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike),
  timeoutMs: WORKER_TIMEOUT_MS,
  local: { detect: runDetection, estimate: runEstimation, diagnose: runDiagnosis, census: runCensus, runs: runRuns }
})

export const detect = client.detect

export const estimate = client.estimate

export const diagnose = client.diagnose

export const census = client.census

export const readRuns = client.readRuns
